import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import FinanceNodalAdmin from "./shared/FinanceNodalAdmin";
import {
  PageHeader,
  SectionCard,
  WarningBanner,
  ApproverRoleBadge,
} from "./shared/components";
import {
  getNoDuesSettlementInbox,
  getNoDuesSettlementProcessed,
  getFinanceMyWork,
  getUserCapabilities,
  formatDate,
  NoDuesSettlement,
  FinanceMyWork,
} from "./services/eSeparation.service";

type Tab = "Pending" | "Processed";

const NoDuesSettlementInbox: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;

  const [tab, setTab] = useState<Tab>("Pending");
  const [pending, setPending] = useState<NoDuesSettlement[]>([]);
  const [processed, setProcessed] = useState<NoDuesSettlement[]>([]);
  const [loading, setLoading] = useState(true);
  // Finance recovery flow: what is open for the caller as recovery head / manager,
  // and whether they are the nodal (who also gets the admin panels).
  const [work, setWork] = useState<FinanceMyWork>({ as_head: [], as_manager: [] });
  const [isNodal, setIsNodal] = useState(false);

  const load = async () => {
    setLoading(true);
    const [inbox, done, mine, caps] = await Promise.all([
      getNoDuesSettlementInbox(),
      getNoDuesSettlementProcessed(),
      getFinanceMyWork(),
      getUserCapabilities(),
    ]);
    setPending(inbox.data || []);
    setProcessed(done.data || []);
    setWork(mine.data || { as_head: [], as_manager: [] });
    setIsNodal(!!caps.roles.is_finance_approver);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const openAction = (row: NoDuesSettlement) => {
    navigate(
      `${routes.Esepration.noDuesSettlementAction}/${encodeURIComponent(
        row.request_id
      )}`,
      { state: { request_id: row.request_id } }
    );
  };

  const openRequest = (requestId: string) =>
    navigate(`${routes.Esepration.noDuesSettlementAction}/${encodeURIComponent(requestId)}`, {
      state: { request_id: requestId },
    });

  const rows = tab === "Pending" ? pending : processed;
  const showSettlements = isNodal || pending.length > 0 || processed.length > 0;

  return (
    <div className="page-wrapper">
      <div className="content">
        <PageHeader
          title="Finance Settlement"
          subtitle="No-Dues settlements awaiting your finance action"
          right={<ApproverRoleBadge role="Finance" label="Settlement Dept" />}
          onExit={() => navigate(-1)}
        />

        {/* As a recovery head: checklists waiting for you */}
        {work.as_head.length > 0 && (
          <SectionCard
            index={0}
            title="My Recovery Checklists"
            subtitle="Fill the amount due and a comment for each of your items, then submit."
          >
            <div className="row g-3">
              {work.as_head.map((w) => (
                <div className="col-md-6 col-xl-4" key={w.request_id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{ borderRadius: 14, cursor: "pointer" }}
                    onClick={() => openRequest(w.request_id)}
                  >
                    <div className="card-body">
                      <div className="fw-semibold">{w.emp_name || "—"} <span className="text-muted">({w.emp_no || "—"})</span></div>
                      <small className="text-muted d-block mb-2">{w.request_id}</small>
                      <div className="d-flex flex-wrap gap-1 mb-3">
                        {w.tasks.map((t) => (
                          <span
                            key={t.id}
                            className="badge"
                            style={{
                              background: t.status === "LOCKED" ? "#EEEDEB" : "#FFF1DE",
                              color: t.status === "LOCKED" ? "#78746D" : "#BA7517",
                            }}
                          >
                            {t.group_name}{t.status === "LOCKED" ? " · locked" : ""}
                          </span>
                        ))}
                      </div>
                      <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openRequest(w.request_id); }}>
                        <i className="ti ti-edit me-1" />
                        Open Checklist
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* As an Establishment manager: requests to verify */}
        {work.as_manager.length > 0 && (
          <SectionCard
            index={1}
            title="To Verify (Establishment)"
            subtitle="Review the completed checklist and add your comment."
          >
            <div className="row g-3">
              {work.as_manager.map((w) => (
                <div className="col-md-6 col-xl-4" key={w.request_id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{ borderRadius: 14, cursor: "pointer" }}
                    onClick={() => openRequest(w.request_id)}
                  >
                    <div className="card-body">
                      <div className="fw-semibold">{w.emp_name || "—"} <span className="text-muted">({w.emp_no || "—"})</span></div>
                      <small className="text-muted d-block mb-2">{w.request_id} · {w.label}</small>
                      <span
                        className="badge mb-3 d-inline-block"
                        style={{
                          background: w.open ? "#E4F7E8" : "#FFF1DE",
                          color: w.open ? "#049123" : "#BA7517",
                        }}
                      >
                        {w.open ? "Ready to verify" : "Waiting for recovery heads"}
                      </span>
                      <div>
                        <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openRequest(w.request_id); }}>
                          <i className="ti ti-eye me-1" />
                          Open
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* The Finance nodal's settlements + admin rights */}
        {showSettlements && (<>
        {/* Sub-header */}
        <div className="d-flex align-items-center gap-2 mb-3">
          <span
            className="d-inline-flex align-items-center justify-content-center"
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: "#FDE8E0",
              color: "#993C1D",
            }}
          >
            <i className="ti ti-cash" style={{ fontSize: 18 }} />
          </span>
          <div>
            <div className="fw-semibold">Consolidated Recovery</div>
            <small className="text-muted">
              Track the recovery heads and Establishment managers, then sign off and forward to HR
            </small>
          </div>
        </div>

        {/* Pending / Processed tabs */}
        <div
          className="d-flex p-1 mb-3"
          style={{ background: "rgba(0,0,0,0.04)", borderRadius: 10 }}
        >
          {[
            { key: "Pending", label: `Pending (${pending.length})` },
            { key: "Processed", label: `Processed (${processed.length})` },
          ].map((t) => (
            <button
              key={t.key}
              className="btn flex-fill fw-semibold"
              style={{
                borderRadius: 8,
                background: tab === t.key ? "#fff" : "transparent",
                color: tab === t.key ? "#993C1D" : "#6c757d",
                boxShadow: tab === t.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
              }}
              onClick={() => setTab(t.key as Tab)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-secondary" role="status" />
            <p className="text-muted mt-3 mb-0">Loading settlement inbox...</p>
          </div>
        ) : rows.length === 0 ? (
          <SectionCard index={0} title={`${tab} Settlements`}>
            <WarningBanner
              type={tab === "Pending" ? "success" : "info"}
              message={
                tab === "Pending"
                  ? "No pending settlements on your desk."
                  : "No closed settlements yet."
              }
            />
          </SectionCard>
        ) : (
          <div className="row g-3">
            {rows.map((row) => {
              const closed = String(row.status).toUpperCase() === "CLOSED";
              const meta = closed
                ? { label: "Closed", bg: "#E4F7E8", color: "#049123" }
                : { label: "In Progress", bg: "#FC9D4520", color: "#BA7517" };
              return (
                <div className="col-md-6 col-xl-4" key={row.id}>
                  <div
                    className="card border-0 shadow-sm h-100"
                    style={{ borderRadius: 14, cursor: "pointer" }}
                    onClick={() => openAction(row)}
                  >
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="flex-grow-1 overflow-hidden">
                          <div className="fw-semibold text-truncate">
                            {row.request_id}
                          </div>
                          <small className="text-muted text-truncate d-block">
                            {row.department_name}
                            {row.dispatched_at
                              ? ` • ${formatDate(row.dispatched_at)}`
                              : ""}
                          </small>
                        </div>
                        <span
                          className="badge d-inline-flex align-items-center gap-1"
                          style={{ background: meta.bg, color: meta.color }}
                        >
                          <i className="ti ti-clock" />
                          {meta.label}
                        </span>
                      </div>

                      <div className="small fw-medium mb-2" style={{ color: "#993C1D" }}>
                        Due: ₹{Number(row.total_due).toLocaleString("en-IN")}
                        {closed
                          ? `  •  Collected: ₹${Number(
                              row.total_collected
                            ).toLocaleString("en-IN")}`
                          : ""}
                      </div>

                      <button
                        className="btn text-white mt-auto"
                        style={{ background: "#993C1D" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          openAction(row);
                        }}
                      >
                        <i className="ti ti-edit me-1" />
                        {tab === "Pending"
                          ? "Open Settlement"
                          : "View Settlement"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>)}

        {isNodal && <FinanceNodalAdmin onChanged={load} />}

        {!loading && !showSettlements && work.as_head.length === 0 && work.as_manager.length === 0 && (
          <WarningBanner type="success" message="Nothing is waiting for you right now." />
        )}
      </div>
    </div>
  );
};

export default NoDuesSettlementInbox;
